import React, { MouseEventHandler, ReactNode, useEffect, useState } from 'react';

import Contact from '@models/Contact';
import {
  selectContactProfileUpdateStatus,
  selectIntegrationByPlatform,
  selectIntegrationStatusOnDevice,
} from '@reducers/user/integrations';
import { useIsElectron } from '@support/hooks/use-is-electron';
import { useDispatch, useSelector } from 'react-redux';
import styles from './RefreshProfile.module.scss';
import { CheckIcon, CircledExclamationPointIcon, LoaderIcon, SyncIcon } from '@components/Icons';
import Tooltip, { Overlay } from '@components/Tooltip';
import { syncDeviceIntegration, updateContactProfile } from '@actions/deviceIntegrations';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';

function RefreshErrorDisplay({ contact }: { contact: Contact }) {
  const integrationStateOnDevice = useSelector(selectIntegrationStatusOnDevice('linkedin'));
  const profileUpdateStatus = useSelector(selectContactProfileUpdateStatus(contact.id));
  const [reconnectingLinkedIn, setReconnectingLinkedIn] = useState(false);

  let onClick: MouseEventHandler = (e) => {
    e.preventDefault();
    setReconnectingLinkedIn(true);
    syncDeviceIntegration('linkedin');
  };
  let overlayMessage: ReactNode = (
    <>
      An error occurred while
      <br />
      refreshing this profile
    </>
  );

  useEffect(() => {
    if (
      reconnectingLinkedIn &&
      (!integrationStateOnDevice.syncing || integrationStateOnDevice.enabled)
    ) {
      setReconnectingLinkedIn(false);
    }
  }, [integrationStateOnDevice.syncing, integrationStateOnDevice.enabled]);

  if (integrationStateOnDevice.enabled && !integrationStateOnDevice.error && !profileUpdateStatus) {
    return null;
  }

  if (
    profileUpdateStatus?.type === 'updating' ||
    (profileUpdateStatus?.type === 'success' && !profileUpdateStatus?.message)
  ) {
    return null;
  }

  if (!integrationStateOnDevice.enabled) {
    overlayMessage = (
      <>
        Click to connect LinkedIn and
        <br />
        enable profile refresh
      </>
    );
  }

  if (integrationStateOnDevice.error && !integrationStateOnDevice.enabled) {
    overlayMessage = (
      <>
        An error occurred when trying
        <br />
        to connect to LinkedIn.
        <br />
        Click to try again.
      </>
    );
  } else if (integrationStateOnDevice.error) {
    overlayMessage = (
      <>
        Sorry, an error occurred with
        <br />
        your LinkedIn integration.
        <br />
        Click to try reconnecting.
      </>
    );
  } else if (profileUpdateStatus?.type === 'error') {
    overlayMessage = (
      <>
        An error occurred while
        <br />
        refreshing this profile
      </>
    );
    onClick = undefined;
  } else if (profileUpdateStatus?.type === 'success') {
    overlayMessage = (
      <>
        We were only able to partially
        <br />
        update this profile
      </>
    );
    onClick = undefined;
  }

  if (reconnectingLinkedIn) {
    return (
      <div className={styles.error}>
        <LoaderIcon color="textPrimary" />
      </div>
    );
  }

  return (
    <Tooltip placement="left" overlay={<Overlay>{overlayMessage}</Overlay>}>
      <div className={styles.error} onClick={onClick}>
        <CircledExclamationPointIcon color="textPrimary" width="16" height="16" />
      </div>
    </Tooltip>
  );
}

const DefaultBtnContent = () => (
  <span className={styles.btnContent}>
    Refresh Profile <SyncIcon color="textPrimary" width="10.5" />
  </span>
);

const LoadingBtnContent = () => (
  <span className={styles.btnContent}>
    Refreshing Profile <LoaderIcon color="textPrimary" width="10" height="10" />
  </span>
);

const SuccessBtnContent = () => (
  <span className={styles.btnContent}>
    Profile Refreshed <CheckIcon color="textPrimary" width="9" strokeWidth="2.4" />
  </span>
);

export default function RefreshProfile({ contact }: { contact: Contact }) {
  const dispatch = useDispatch();
  const isElectron = useIsElectron();
  const integration = useSelector(selectIntegrationByPlatform('linkedin'));
  const integrationStateOnDevice = useSelector(selectIntegrationStatusOnDevice('linkedin'));
  const profileUpdateStatus = useSelector(selectContactProfileUpdateStatus(contact.id));

  if (!isElectron || !contact.linkedin) {
    return null;
  }

  return (
    <div className={styles.refreshProfile}>
      <RefreshErrorDisplay contact={contact} />
      <button
        className={styles.refreshBtn}
        onClick={(e) => {
          e.preventDefault();
          dispatch(
            updateContactProfile({ contactID: contact.id, linkedinHandle: contact.linkedin }),
          );
        }}
        disabled={
          !integration ||
          !integrationStateOnDevice.enabled ||
          ['updating', 'success'].includes(profileUpdateStatus?.type)
        }
      >
        <StateBasedFadeTransition
          state={profileUpdateStatus?.type || 'default'}
          elementByStateMap={{
            updating: <LoadingBtnContent />,
            success: <SuccessBtnContent />,
            error: <DefaultBtnContent />,
            default: <DefaultBtnContent />,
          }}
          duration="fast"
        />
      </button>
      <p>Re-import profile data from LinkedIn</p>
    </div>
  );
}
